import type { PresentationOptions } from "../types/proposalPresentation";

type RowKey = "findings" | "comparison" | "milestones" | "credentials";
export const presentationRowGroups: {
  key: RowKey;
  title: string;
  rowLabel: string;
  fields: {
    key: string;
    label: string;
    maxLength?: number;
    required?: boolean;
  }[];
}[] = [
  {
    key: "findings",
    title: "Inspection findings",
    rowLabel: "Finding",
    fields: [
      { key: "title", label: "Finding", maxLength: 200, required: true },
      { key: "location", label: "Location", maxLength: 200 },
      { key: "impact", label: "Impact / recommended work", maxLength: 3000 },
    ],
  },
  {
    key: "comparison",
    title: "Package comparison",
    rowLabel: "Comparison row",
    fields: [
      { key: "label", label: "Feature", maxLength: 200, required: true },
      { key: "good", label: "Good tier", maxLength: 500 },
      { key: "better", label: "Better tier", maxLength: 500 },
      { key: "best", label: "Best tier", maxLength: 500 },
    ],
  },
  {
    key: "milestones",
    title: "Project & payment milestones",
    rowLabel: "Milestone",
    fields: [
      { key: "title", label: "Milestone", maxLength: 200, required: true },
      { key: "description", label: "Description", maxLength: 2000 },
      { key: "percent", label: "Payment % (optional)" },
    ],
  },
  {
    key: "credentials",
    title: "Company credentials",
    rowLabel: "Credential",
    fields: [
      { key: "label", label: "Label", maxLength: 80, required: true },
      {
        key: "value",
        label: "Verified detail",
        maxLength: 250,
        required: true,
      },
    ],
  },
];

/** Match presentation authoring rules before attempting a server save. */
export function presentationDraftIssues(value: PresentationOptions = {}) {
  const issues: Record<string, string> = {};
  for (const group of presentationRowGroups) {
    const rows = value[group.key] || [];
    if (rows.length > 100)
      issues[group.key] = `${group.title}: use at most 100 rows.`;
    rows.forEach((row, index) => {
      for (const field of group.fields) {
        const raw = (row as unknown as Record<string, unknown>)[field.key];
        const text = String(raw ?? "").trim();
        const path = `${group.key}.${index}.${field.key}`;
        const label = `${group.rowLabel} ${index + 1} · ${field.label}`;
        if (field.required && !text)
          issues[path] = `${label}: required to save.`;
        else if (field.maxLength && Array.from(text).length > field.maxLength)
          issues[path] = `${label}: use at most ${field.maxLength} characters.`;
        if (
          field.key === "percent" &&
          raw !== undefined &&
          (!/^\d+(\.\d{1,2})?$/.test(text) || Number(text) > 100)
        )
          issues[path] = `${label}: enter 0–100 with up to two decimal places.`;
      }
    });
  }
  const payments = (value.milestones || []).filter(
    (row) => row.percent !== undefined,
  );
  if (
    payments.length &&
    !Object.keys(issues).some((path) => path.endsWith(".percent"))
  ) {
    const total = payments.reduce(
      (sum, row) => sum + Math.round(Number(row.percent) * 100),
      0,
    );
    if (total !== 10000)
      issues.milestones = `Payment percentages total ${total / 100}%. Adjust them to 100% to save.`;
  }
  if (
    value.cover_position?.some(
      (position) =>
        !Number.isInteger(position) || position < 0 || position > 100,
    )
  )
    issues.cover_position =
      "Cover photo position: enter whole numbers from 0 to 100.";
  if (Array.from((value.next_steps || "").trim()).length > 4000)
    issues.next_steps = "Next steps: use at most 4000 characters.";
  return issues;
}

/** Keep server field details visible instead of reducing every rejection to HTTP 400. */
export function proposalSaveError(error: unknown): string {
  const data = (error as { responseData?: unknown } | null)?.responseData;
  const messages: string[] = [];
  const visit = (value: unknown, path: string[]) => {
    if (typeof value === "string") {
      messages.push(`${path.length ? `${path.join(" · ")}: ` : ""}${value}`);
    } else if (Array.isArray(value)) {
      value.forEach((entry, index) =>
        visit(
          entry,
          typeof entry === "string" ? path : [...path, `Row ${index + 1}`],
        ),
      );
    } else if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, entry]) =>
        visit(
          entry,
          ["error", "detail", "non_field_errors"].includes(key)
            ? path
            : [...path, key.replaceAll("_", " ")],
        ),
      );
    }
  };
  visit(data, []);
  return (
    messages[0] ||
    (error instanceof Error ? error.message : "Changes could not be saved.")
  );
}
